import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { CarCard, CarCardSkeleton } from './components/CarCard';
import { fetchInventoryCatalog, filterCarCatalog, mergeFacets } from './carInventoryService';
import { CarFilterChecklist } from './components/CarFilterChecklist';
import { CarRangeSlider } from './components/CarRangeSlider';
import {
  CAR_SORT_OPTIONS,
  CAR_STATUS_OPTIONS,
  formatBodyStyle,
  formatCarPriceAmount,
  inventoryKmExtent,
  inventoryPriceExtent,
  modelsForMake,
  yearsForMake,
  type CarListItem,
  type CarListResponse,
  type CarListingStatus,
  type CarSort,
} from './carInventoryTypes';
import '../store/StorePage.css';
import './CarInventory.css';

const PAGE_SIZES = [25, 50, 100] as const;
type CarPageSize = (typeof PAGE_SIZES)[number];
const DEFAULT_PAGE_SIZE: CarPageSize = 25;
const SORT_VALUES = new Set<string>(CAR_SORT_OPTIONS.map((o) => o.value));

function parsePageSize(raw: string): CarPageSize {
  const n = Number(raw);
  return PAGE_SIZES.includes(n as CarPageSize) ? (n as CarPageSize) : DEFAULT_PAGE_SIZE;
}

function parsePage(raw: string): number {
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 1 ? n : 1;
}

function withSelected(options: string[], selected: string): string[] {
  if (!selected) return options;
  if (options.some((option) => option.toLowerCase() === selected.toLowerCase())) return options;
  return [selected, ...options];
}

function sliderValue(raw: string, fallback: number, min: number, max: number): number {
  if (!raw) return fallback;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
}

function FilterField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="car-filter-field">
      <span className="car-filter-label">{label}</span>
      {children}
    </div>
  );
}

function CarListPager({
  page,
  totalPages,
  total,
  limit,
  onPage,
  onLimit,
}: {
  page: number;
  totalPages: number;
  total: number;
  limit: CarPageSize;
  onPage: (page: number) => void;
  onLimit: (limit: CarPageSize) => void;
}) {
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  return (
    <div className="car-pager">
      <label className="car-pager-size">
        Rows per page
        <select
          className="car-select car-select--pager"
          value={limit}
          onChange={(e) => onLimit(parsePageSize(e.target.value))}
          aria-label="Rows per page"
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>
      </label>
      <span className="car-pager-range">
        {from}–{to} of {total}
      </span>
      <span className="car-pager-status">Page {page} of {totalPages}</span>
      <div className="car-pager-nav">
        <button type="button" aria-label="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Previous
        </button>
        <button type="button" aria-label="Next page" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>
          Next
        </button>
      </div>
    </div>
  );
}

export const CarListingPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const make = searchParams.get('make') ?? '';
  const model = searchParams.get('model') ?? '';
  const yearMin = searchParams.get('year_min') ?? '';
  const yearMax = searchParams.get('year_max') ?? '';
  const kmMin = searchParams.get('km_min') ?? '';
  const kmMax = searchParams.get('km_max') ?? '';
  const priceMin = searchParams.get('price_min') ?? '';
  const priceMax = searchParams.get('price_max') ?? '';
  const bodyStyle = searchParams.get('body') ?? '';
  const sortParam = searchParams.get('sort') ?? '';
  const sort: CarSort = SORT_VALUES.has(sortParam) ? (sortParam as CarSort) : 'newest';
  const statusParam = searchParams.get('status') ?? '';
  const status: CarListingStatus =
    statusParam === 'for_sale' || statusParam === 'sold' ? statusParam : 'all';
  const limit = parsePageSize(searchParams.get('limit') ?? '');
  const page = parsePage(searchParams.get('page') ?? '');

  const [searchInput, setSearchInput] = useState(q);
  const [catalog, setCatalog] = useState<CarListItem[]>([]);
  const [facets, setFacets] = useState<CarListResponse['facets']>({
    makes: [],
    models: [],
    years: [],
    body_styles: [],
    models_by_make: {},
    years_by_make: {},
  });
  const [counts, setCounts] = useState<CarListResponse['counts']>({ for_sale: 0, sold: 0, all: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const queryVersion = useRef(0);
  const skipScrollRef = useRef(true);

  const patchParams = useCallback(
    (patch: Record<string, string>, resetPage = true) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value) next.set(key, value);
            else next.delete(key);
          }
          if (resetPage) next.delete('page');
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const updateParam = useCallback(
    (key: string, value: string) => {
      patchParams({ [key]: value }, true);
    },
    [patchParams]
  );

  const goToPage = useCallback(
    (nextPage: number) => {
      patchParams({ page: nextPage <= 1 ? '' : String(nextPage) }, false);
    },
    [patchParams]
  );

  const changeLimit = useCallback(
    (nextLimit: CarPageSize) => {
      patchParams({ limit: nextLimit === DEFAULT_PAGE_SIZE ? '' : String(nextLimit) }, true);
    },
    [patchParams]
  );

  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const timer = setTimeout(() => updateParam('q', trimmed), 350);
    return () => clearTimeout(timer);
  }, [searchInput, q, updateParam]);

  useEffect(() => {
    const controller = new AbortController();
    const version = ++queryVersion.current;
    setLoading(true);
    setError(null);
    fetchInventoryCatalog(status, controller.signal, (partial) => {
        if (version !== queryVersion.current) return;
        setCatalog(partial.cars);
        setFacets((prev) => mergeFacets(prev, partial.facets));
        setCounts(partial.counts ?? { for_sale: 0, sold: 0, all: 0 });
        setLoading(false);
      })
      .then((data) => {
        if (version !== queryVersion.current) return;
        setCatalog(data.cars);
        setFacets((prev) => mergeFacets(prev, data.facets));
        setCounts(data.counts ?? { for_sale: 0, sold: 0, all: 0 });
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error('[CarListingPage] load failed', err);
        setError('Unable to load vehicles. Please try again.');
      })
      .finally(() => {
        if (version === queryVersion.current) setLoading(false);
      });
    return () => controller.abort();
  }, [status, reloadKey]);

  useEffect(() => {
    if (skipScrollRef.current) {
      skipScrollRef.current = false;
      return;
    }
    document.querySelector('.car-grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [page, limit]);

  const selectedModels = useMemo(
    () => model.split(',').map((value) => value.trim()).filter(Boolean),
    [model]
  );
  const filteredCars = useMemo(() => {
    const matched = filterCarCatalog(catalog, {
      q,
      make,
      models: selectedModels,
      yearMin,
      yearMax,
      kmMin,
      kmMax,
      priceMin,
      priceMax,
      bodyStyle,
    });
    const copy = [...matched];
    copy.sort((a, b) => {
      const soldOrder = Number(Boolean(a.is_sold)) - Number(Boolean(b.is_sold));
      if (soldOrder) return soldOrder;
      if (sort === 'price_asc') return (a.price ?? Number.POSITIVE_INFINITY) - (b.price ?? Number.POSITIVE_INFINITY);
      if (sort === 'price_desc') return (b.price ?? Number.NEGATIVE_INFINITY) - (a.price ?? Number.NEGATIVE_INFINITY);
      if (sort === 'year_desc') return (b.year ?? 0) - (a.year ?? 0);
      if (sort === 'km_asc') {
        return (a.odometer_km ?? Number.POSITIVE_INFINITY) - (b.odometer_km ?? Number.POSITIVE_INFINITY);
      }
      return 0;
    });
    return copy;
  }, [catalog, q, make, selectedModels, yearMin, yearMax, kmMin, kmMax, priceMin, priceMax, bodyStyle, sort]);
  const total = filteredCars.length;
  const cars = filteredCars.slice((page - 1) * limit, page * limit);
  const hasFilters = Boolean(
    q || make || selectedModels.length || yearMin || yearMax || kmMin || kmMax || priceMin || priceMax || bodyStyle
  );
  const clearFilters = () => {
    setSearchInput('');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams();
        const keepStatus = prev.get('status');
        const keepSort = prev.get('sort');
        const keepLimit = prev.get('limit');
        if (keepStatus) next.set('status', keepStatus);
        if (keepSort) next.set('sort', keepSort);
        if (keepLimit) next.set('limit', keepLimit);
        return next;
      },
      { replace: true }
    );
  };

  const heading =
    status === 'sold' ? 'Sold vehicles' : status === 'all' ? 'All vehicles' : 'Vehicles for sale';
  const subcopy =
    status === 'sold'
      ? 'These listings have already sold. Open one to see the last photos and details.'
      : status === 'all'
        ? 'Available cars are listed first. Sold cars follow at the end, marked so they stay easy to tell apart.'
        : 'Browse pre-owned cars available now. Open any listing to see the full photo gallery and details.';
  const resultLabel =
    status === 'sold' ? 'sold' : status === 'all' ? 'in inventory' : 'available';
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const showPager = !error && total > 0;
  const modelOptions = useMemo(
    () => withSelected(modelsForMake(make, facets, catalog), model),
    [make, facets, catalog, model]
  );
  const yearOptions = useMemo(
    () => withSelected(withSelected(yearsForMake(make, facets, catalog).map(String), yearMin), yearMax),
    [make, facets, catalog, yearMin, yearMax]
  );
  const priceExtent = useMemo(() => inventoryPriceExtent(catalog), [catalog]);
  const kmExtent = useMemo(() => inventoryKmExtent(catalog), [catalog]);
  const priceLo = priceExtent
    ? sliderValue(priceMin, priceExtent.min, priceExtent.min, priceExtent.max)
    : 0;
  const priceHi = priceExtent
    ? sliderValue(priceMax, priceExtent.max, priceExtent.min, priceExtent.max)
    : 0;
  const kmLo = kmExtent ? sliderValue(kmMin, kmExtent.min, kmExtent.min, kmExtent.max) : 0;
  const kmHi = kmExtent ? sliderValue(kmMax, kmExtent.max, kmExtent.min, kmExtent.max) : 0;

  useEffect(() => {
    const pages = Math.max(1, Math.ceil(total / limit));
    if (!loading && page > pages) goToPage(pages);
  }, [loading, page, total, limit, goToPage]);

  return (
    <div className="store-page car-page">
      <motion.div
        className="saas-sec-head"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
      >
        <div className="saas-sec-head-left">
          <span className="saas-sec-eyebrow">
            <span className="saas-sec-eyebrow-line" />
            CWY Vehicles
          </span>
          <h1 className="saas-sec-title">{heading}</h1>
          <p className="saas-sec-sub">{subcopy}</p>
        </div>
      </motion.div>

      <div className="car-layout">
        <aside className="car-filters" aria-label="Vehicle filters">
          <div className="car-filters-head">
            <h2>Filter by</h2>
            {hasFilters && (
              <button className="car-link-btn" onClick={clearFilters}>
                <i className="ti ti-x" aria-hidden /> Clear
              </button>
            )}
          </div>

          <FilterField label="Search">
            <label className="car-search">
              <i className="ti ti-search" aria-hidden />
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Year, make, model or stock #"
                aria-label="Search vehicles"
              />
            </label>
          </FilterField>

          <FilterField label="Make">
            <select
              className={`car-select${make ? ' car-select--active' : ''}`}
              value={make}
              onChange={(e) => patchParams({ make: e.target.value, model: '' }, true)}
              aria-label="Filter by make"
            >
              <option value="">All makes</option>
              {withSelected(facets.makes, make).map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </FilterField>

          <div className="car-year-row">
            <FilterField label="Min year">
              <select
                className={`car-select${yearMin ? ' car-select--active' : ''}`}
                value={yearMin}
                onChange={(e) => updateParam('year_min', e.target.value)}
                aria-label="Minimum year"
              >
                <option value="">Any</option>
                {yearOptions.map((year) => (
                  <option key={`min-${year}`} value={year}>{year}</option>
                ))}
              </select>
            </FilterField>
            <FilterField label="Max year">
              <select
                className={`car-select${yearMax ? ' car-select--active' : ''}`}
                value={yearMax}
                onChange={(e) => updateParam('year_max', e.target.value)}
                aria-label="Maximum year"
              >
                <option value="">Any</option>
                {yearOptions.map((year) => (
                  <option key={`max-${year}`} value={year}>{year}</option>
                ))}
              </select>
            </FilterField>
          </div>

          {priceExtent && (
            <CarRangeSlider
              label="Price"
              min={priceExtent.min}
              max={priceExtent.max}
              step={1}
              valueMin={priceLo}
              valueMax={priceHi}
              formatValue={formatCarPriceAmount}
              minAriaLabel="Minimum price"
              maxAriaLabel="Maximum price"
              onChange={(nextMin, nextMax) => {
                patchParams({
                  price_min: nextMin <= priceExtent.min ? '' : String(nextMin),
                  price_max: nextMax >= priceExtent.max ? '' : String(nextMax),
                });
              }}
            />
          )}

          {kmExtent && (
            <CarRangeSlider
              label="Odometer"
              min={kmExtent.min}
              max={kmExtent.max}
              step={1}
              valueMin={kmLo}
              valueMax={kmHi}
              formatValue={(km) => km.toLocaleString('en-CA')}
              formatRange={(lo, hi) =>
                `${lo.toLocaleString('en-CA')} to ${hi.toLocaleString('en-CA')} km`
              }
              minAriaLabel="Minimum kilometres"
              maxAriaLabel="Maximum kilometres"
              onChange={(nextMin, nextMax) => {
                patchParams({
                  km_min: nextMin <= kmExtent.min ? '' : String(nextMin),
                  km_max: nextMax >= kmExtent.max ? '' : String(nextMax),
                });
              }}
            />
          )}

          {facets.body_styles.length > 0 && (
            <FilterField label="Body style">
              <select
                className={`car-select${bodyStyle ? ' car-select--active' : ''}`}
                value={bodyStyle}
                onChange={(e) => updateParam('body', e.target.value)}
                aria-label="Filter by body style"
              >
                <option value="">All types</option>
                {withSelected(facets.body_styles, bodyStyle).map((style) => (
                  <option key={style} value={style}>{formatBodyStyle(style)}</option>
                ))}
              </select>
            </FilterField>
          )}

          <FilterField label="Availability">
            <div className="car-filter-pills">
              {CAR_STATUS_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`store-filter-pill${status === option.value ? ' store-filter-pill--active' : ''}`}
                  onClick={() => updateParam('status', option.value === 'all' ? '' : option.value)}
                >
                  {option.label}
                  {counts[option.value] > 0 ? ` (${counts[option.value]})` : ''}
                </button>
              ))}
            </div>
          </FilterField>

          <CarFilterChecklist
            title="Model"
            searchPlaceholder="Filter Model Options"
            name="vehicle-model"
            options={modelOptions}
            selected={selectedModels}
            onChange={(next) => patchParams({ model: next.join(',') }, true)}
          />
        </aside>

        <div className="car-results">
          <div className="car-results-toolbar">
            {!loading && !error && (
              <span>
                {total} {total === 1 ? 'vehicle' : 'vehicles'} {hasFilters ? 'match your filters' : resultLabel}
              </span>
            )}
            <select
              className="car-select"
              value={sort}
              onChange={(e) => updateParam('sort', e.target.value === 'newest' ? '' : e.target.value)}
              aria-label="Sort vehicles"
            >
              {CAR_SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {error && (
            <div className="store-error-banner">
              <i className="ti ti-alert-circle" style={{ fontSize: 16 }} />
              {error}
              <button className="store-error-retry" onClick={() => setReloadKey((k) => k + 1)}>Retry</button>
            </div>
          )}

          <div className="car-grid">
            {loading
              ? Array.from({ length: 8 }, (_, k) => <CarCardSkeleton key={k} />)
              : cars.map((car, i) => <CarCard key={car.id} car={car} index={i % limit} />)}
          </div>

          {showPager && (
            <CarListPager
              page={page}
              totalPages={totalPages}
              total={total}
              limit={limit}
              onPage={goToPage}
              onLimit={changeLimit}
            />
          )}

          {!loading && !error && cars.length === 0 && (
            <motion.div
              className="store-empty"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35 }}
            >
              <i className="ti ti-car-off" style={{ fontSize: 40, color: '#ccc' }} />
              <p className="store-empty-text">
                {hasFilters ? 'No vehicles match your filters.' : 'No vehicles are listed right now — check back soon.'}
              </p>
              {hasFilters && (
                <button className="store-empty-btn" onClick={clearFilters}>Clear filters</button>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};
